import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  ButtonBase,
  Collapse,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { resources } from '../../../core/services/resources';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { MarkdownContent } from '../../../shared/components/MarkdownContent';
import { Pill } from '../../../shared/components/Pill';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import type { EntityTypeEnrichment, EntityTypeEnrichmentSection } from '../../../core/types/data-models';
import type { EntityCardData, MentionView } from './useEntityTypeEntities';

/** Lines of section text shown per mention before it is cut off. */
const MENTION_PREVIEW_LINES = 4;

interface EntityDetailPanelProps {
  entity: EntityCardData;
  /** The type's ENTITY_TYPE-scope insight topics — their sections that name the entity are listed under "Mentioned In". */
  topics: EntityTypeEnrichment[];
  /** ENTITY-scope insight topics generated for this specific entity (entityId === entity.id) — shown in full under "Entity Analyses". */
  entityTopics: EntityTypeEnrichment[];
  /** Ids of the entities on this page, so a related entity of the same type opens here instead of reading as plain text. */
  selectableEntityIds: Set<number>;
  onSelectEntity: (entityId: number) => void;
  /** Opens the topic as its own tab. */
  onViewTopic: (topicId: number) => void;
  onClose: () => void;
}

/**
 * Right-side detail panel for the entity selected on the Main tab — the full
 * version of what EntityKnowledgeCard summarises as counts: its properties,
 * related entities, every document mention with a link to the source
 * document, and two kinds of "Analyses": the ENTITY-scope topics generated
 * for this entity by name (`entityTopics`, shown in full), and the
 * ENTITY_TYPE-scope topics' sections that happen to mention it (`topics`,
 * matched by text since those sections carry no entity link).
 */
export function EntityDetailPanel({
  entity,
  topics,
  entityTopics,
  selectableEntityIds,
  onSelectEntity,
  onViewTopic,
  onClose,
}: EntityDetailPanelProps) {
  // One fetch for both kinds of topic's sections, keyed on the combined topic
  // ids rather than the entity, so switching entities reuses it when the
  // topic lists are unchanged; a regenerated topic gets a new id and refetches.
  const topicIds = useMemo(
    () => Array.from(new Set([...topics, ...entityTopics].map((t) => t.id))),
    [topics, entityTopics],
  );
  const sectionsQuery = useQuery({
    queryKey: ['managed-knowledge-enrichment-sections-by-topic', topicIds],
    queryFn: () =>
      resources.entityTypeEnrichmentSections.list({
        filter: { entityTypeEnrichmentId: topicIds },
        sort: 'order',
        pageSize: 2000,
      }),
    enabled: topicIds.length > 0,
  });
  const sections = sectionsQuery.data?.data ?? [];

  // Entity Analyses: ENTITY-scope topics generated for this exact entity —
  // every section is shown, no text matching needed.
  const entityTopicSections = entityTopics.map((topic) => ({
    topic,
    sections: sections.filter((s) => s.entityTypeEnrichmentId === topic.id),
  }));

  // Mentioned In: ENTITY_TYPE-scope topics whose sections happen to name this
  // entity — a text match, since those sections carry no entity link.
  const needle = entity.name.trim().toLowerCase();
  const matchedByTopic = topics
    .map((topic) => ({
      topic,
      sections: sections.filter(
        (s) =>
          s.entityTypeEnrichmentId === topic.id &&
          needle !== '' &&
          (s.title.toLowerCase().includes(needle) || (s.content ?? '').toLowerCase().includes(needle)),
      ),
    }))
    .filter((g) => g.sections.length > 0);
  const matchedCount = matchedByTopic.reduce((n, g) => n + g.sections.length, 0);
  const totalAnalysesCount = entityTopics.length + matchedCount;

  return (
    <GlassPanel sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
            <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3 }}>
              {entity.name}
            </Typography>
            {entity.isManaged && <Pill color="warning">Managed</Pill>}
          </Stack>
          {entity.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {entity.description}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close entity details" sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider />

      <Box>
        <PanelHeading title="Properties" count={entity.properties.length} />
        {entity.properties.length === 0 ? (
          <EmptyNote>No properties found.</EmptyNote>
        ) : (
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {entity.properties.map((p) => (
              <Box key={p.id}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatEntityTypeLabel(p.key)}:
                  </Box>{' '}
                  {p.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatEntityTypeLabel(p.source)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <PanelHeading title="Related entities" count={entity.relations.length} />
        {entity.relations.length === 0 ? (
          <EmptyNote>No relations found.</EmptyNote>
        ) : (
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {entity.relations.map((r) => (
              <Stack key={r.key} direction="row" spacing={1} alignItems="flex-start">
                <Tooltip title={r.direction === 'outgoing' ? 'Outgoing' : 'Incoming'}>
                  {r.direction === 'outgoing' ? (
                    <ArrowForwardIcon sx={{ fontSize: 15, mt: '2px', color: 'text.disabled', flexShrink: 0 }} />
                  ) : (
                    <ArrowBackIcon sx={{ fontSize: 15, mt: '2px', color: 'text.disabled', flexShrink: 0 }} />
                  )}
                </Tooltip>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {formatEntityTypeLabel(r.relationType)}
                    </Box>{' '}
                    {selectableEntityIds.has(r.otherEntityId) ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onSelectEntity(r.otherEntityId)}
                        sx={{ verticalAlign: 'baseline', fontWeight: 600 }}
                      >
                        {r.otherEntityName}
                      </Link>
                    ) : (
                      <>
                        {r.otherEntityName}{' '}
                        <Typography component="span" variant="caption" color="text.disabled">
                          ({formatEntityTypeLabel(r.otherEntityType)})
                        </Typography>
                      </>
                    )}
                  </Typography>
                  {r.relationDescription && (
                    <Typography variant="caption" color="text.secondary">
                      {r.relationDescription}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <PanelHeading title="Mentions" count={entity.mentions.length} />
        {entity.mentions.length === 0 ? (
          <EmptyNote>No mentions found.</EmptyNote>
        ) : (
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            {entity.mentions.map((m) => {
              const preview = mentionPreview(m);
              return (
                <Box key={m.id}>
                  <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }} noWrap>
                    {m.document?.name ?? 'Untitled document'}
                    {m.section?.title && <> — {m.section.title}</>}
                  </Typography>
                  {preview ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        display: '-webkit-box',
                        WebkitLineClamp: MENTION_PREVIEW_LINES,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {highlightName(preview, entity.name)}
                    </Typography>
                  ) : (
                    <EmptyNote>No content.</EmptyNote>
                  )}
                  {m.document && (
                    <Link
                      component={RouterLink}
                      to={`/documents/${m.document.id}`}
                      variant="caption"
                      sx={{ display: 'inline-block', mt: 0.5, fontWeight: 600 }}
                    >
                      Open document
                    </Link>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <PanelHeading title="Analyses" count={totalAnalysesCount} />
        {entityTopics.length === 0 && topics.length === 0 ? (
          <EmptyNote>No analyses have been run for this entity type yet.</EmptyNote>
        ) : sectionsQuery.isLoading ? (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={28} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {entityTopics.length > 0 && (
              <Stack spacing={1.5}>
                {entityTopicSections.map(({ topic, sections: topicSections }) => (
                  <Box key={topic.id}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {topic.topicTitle}
                    </Typography>
                    {topicSections.length === 0 ? (
                      <EmptyNote>No sections generated.</EmptyNote>
                    ) : (
                      topicSections.map((s) => <AnalysisSectionRow key={s.id} section={s} highlight={entity.name} />)
                    )}
                    <OpenAnalysisLink onClick={() => onViewTopic(topic.id)} />
                  </Box>
                ))}
              </Stack>
            )}

            {matchedByTopic.length > 0 && (
              <Stack spacing={1.5}>
                {entityTopics.length > 0 && (
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Mentioned in
                  </Typography>
                )}
                {matchedByTopic.map(({ topic, sections: topicSections }) => (
                  <Box key={topic.id}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {topic.topicTitle}
                    </Typography>
                    {topicSections.map((s) => (
                      <AnalysisSectionRow key={s.id} section={s} highlight={entity.name} />
                    ))}
                    <OpenAnalysisLink onClick={() => onViewTopic(topic.id)} />
                  </Box>
                ))}
              </Stack>
            )}

            {entityTopics.length === 0 && matchedByTopic.length === 0 && (
              <EmptyNote>No analysis sections mention this entity.</EmptyNote>
            )}
          </Stack>
        )}
      </Box>
    </GlassPanel>
  );
}

/**
 * Plain-text preview of where a mention sits: the section's content with
 * Markdown markers stripped, from the paragraph holding the verbatim span
 * onwards (the whole section when the span isn't found there). A mention
 * whose section didn't resolve — or a document-level one — falls back to
 * the span, then the document description.
 */
function mentionPreview(m: MentionView): string {
  const content = m.section?.content?.trim();
  if (!content) return m.spanText ?? m.document?.description ?? '';
  const paragraphs = content.split(/\n\s*\n/).map(stripMarkdown).filter(Boolean);
  const span = m.spanText ? stripMarkdown(m.spanText).toLowerCase() : '';
  const start = span ? paragraphs.findIndex((p) => p.toLowerCase().includes(span)) : -1;
  return paragraphs.slice(Math.max(start, 0)).join(' ');
}

/** Flattens Markdown to one line of plain text — enough for a clamped preview, not a renderer. */
function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/[*_`~|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Wraps every case-insensitive occurrence of the entity name in a <mark>, same highlight MarkdownContent uses. */
function highlightName(text: string, name: string): ReactNode {
  const needle = name.trim();
  if (!needle) return text;
  const parts = text.split(new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Box key={i} component="mark" sx={{ bgcolor: '#fde047', color: 'inherit', borderRadius: '2px' }}>
        {part}
      </Box>
    ) : (
      part
    ),
  );
}

function PanelHeading({ title, count }: { title: string; count: number }) {
  return (
    <Typography variant="overline" color="text.secondary">
      {title} ({count})
    </Typography>
  );
}

/** Opens an analysis as its own tab — styled like a mention's "Open document" link. */
function OpenAnalysisLink({ onClick }: { onClick: () => void }) {
  return (
    <Link component="button" variant="caption" onClick={onClick} sx={{ display: 'inline-block', mt: 0.5, fontWeight: 600 }}>
      Open analysis
    </Link>
  );
}

function EmptyNote({ children }: { children: string }) {
  return (
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
      {children}
    </Typography>
  );
}

/** One matched insight section — the title row toggles its Markdown body, with the entity name highlighted. */
function AnalysisSectionRow({ section, highlight }: { section: EntityTypeEnrichmentSection; highlight: string }) {
  const [open, setOpen] = useState(false);
  const hasContent = !!section.content?.trim();

  if (!hasContent) {
    return (
      <Typography variant="body2" sx={{ py: 0.5, pl: 2.5 }} noWrap>
        {section.title}
      </Typography>
    );
  }

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ width: '100%', justifyContent: 'flex-start', gap: 0.5, py: 0.5, borderRadius: 1, textAlign: 'left' }}
      >
        {open ? (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--color-muted)', flexShrink: 0 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, color: 'var(--color-muted)', flexShrink: 0 }} />
        )}
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {section.title}
        </Typography>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 2.5, pb: 1 }}>
          <MarkdownContent highlightTerms={[highlight]} sx={{ fontSize: 'var(--font-size-base)' }}>
            {section.content ?? ''}
          </MarkdownContent>
        </Box>
      </Collapse>
    </Box>
  );
}
